import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { catchError, of, switchMap, tap, throwError } from 'rxjs';
import { PricingCountryService } from '../services/pricing-country.service';
import { PricingCountry, PricingCountryList } from '../types/pricing.types';
import {
  AddPricingCountryBundlePriceAction,
  DeletePricingCountryBundlePriceAction,
  GetPricingCountryAction,
  LoadPricingCountriesListAction,
  UpdatePricingCountryAction,
  UpdatePricingCountryBundlePriceAction,
} from './pricing-country.actions';

const PRICING_COUNTRY_STATE_NAME = 'pricingCountries';

export interface PricingCountryStateModel {
  pricingCountries: { [id: string]: PricingCountry };
  pages?: {
    [key: string]: {
      items: string[];
    };
  };
  currentPageSettings: StatePageSettings<PricingCountryFilters>;
  totalCount?: number;
}

@State<PricingCountryStateModel>({
  name: PRICING_COUNTRY_STATE_NAME,
  defaults: {
    pricingCountries: {},
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'name',
        sortDirection: SortDirection.asc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class PricingCountryState {
  private pricingCountryService = inject(PricingCountryService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  @Selector()
  static getPricingCountryById(state: PricingCountryStateModel) {
    return (pricingCountryId: string) =>
      state.pricingCountries[pricingCountryId];
  }

  @Selector([PricingCountryState, PricingCountryState.getCurrentPageIds])
  static getCurrentPage(state: PricingCountryStateModel, ids: string[]) {
    return ids
      .map((id) => state.pricingCountries[id])
      .filter((pricingCountry) => !!pricingCountry);
  }

  @Selector()
  static getCurrentPageIds(state: PricingCountryStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (Object.keys(pages).length === 0 || pageIndex < 0) {
      return [];
    }
    return state.pages[pageIndex]?.items.slice() ?? [];
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
  }: PricingCountryStateModel): Pagination<PricingCountryFilters> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Math.ceil(
        totalCount / currentPageSettings.sortSettings.pageSize
      ),
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(LoadPricingCountriesListAction)
  LoadPricingCountriesListAction(
    { patchState, getState }: StateContext<PricingCountryStateModel>,
    { filters, pageToLoad = 1 }: LoadPricingCountriesListAction
  ) {
    return this.pricingCountryService
      .getPricingCountriesList({ ...filters, page: pageToLoad })
      .pipe(
        tap((pricingCountries: PricingCountryList) => {
          patchState({
            pricingCountries: {
              ...getState().pricingCountries,
              ...pricingCountries.data.reduce((acc, pricingCountry) => {
                acc[pricingCountry.id] = {
                  // keep an already loaded pricelist when the list endpoint
                  // does not return it
                  ...getState().pricingCountries[pricingCountry.id],
                  ...pricingCountry,
                };
                return acc;
              }, {}),
            },
            totalCount: pricingCountries.totalCount,
            pages: {
              ...getState().pages,
              [pageToLoad - 1]: {
                items: pricingCountries.data.map(
                  (pricingCountry: PricingCountry) => pricingCountry.id
                ),
              },
            },
            currentPageSettings: {
              ...getState().currentPageSettings,
              pageIndex: pageToLoad - 1,
              sortSettings: {
                ...getState().currentPageSettings.sortSettings,
                pageSize: filters.limit,
              },
            },
          });
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Forbidden) {
            this.toastService.danger(
              this.translateService.instant(
                'pricing.errors.missingAccess.message'
              ),
              this.translateService.instant(
                'pricing.errors.missingAccess.title'
              ),
              0
            );
          } else {
            this.toastService.danger(
              error.error?.message,
              error.error?.error,
              0
            );
          }

          patchState({
            pricingCountries: {},
            pages: {},
          });

          return throwError(() => error);
        })
      );
  }

  @Action(GetPricingCountryAction)
  GetPricingCountryAction(
    { getState, patchState }: StateContext<PricingCountryStateModel>,
    { id }: GetPricingCountryAction
  ) {
    return this.pricingCountryService.getPricingCountry(id).pipe(
      tap((pricingCountry: PricingCountry) => {
        patchState({
          pricingCountries: {
            ...getState().pricingCountries,
            [pricingCountry.id]: {
              ...getState().pricingCountries[pricingCountry.id],
              ...pricingCountry,
            },
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return of(null);
      })
    );
  }

  @Action(UpdatePricingCountryAction)
  UpdatePricingCountryAction(
    { dispatch }: StateContext<PricingCountryStateModel>,
    { countryId, body }: UpdatePricingCountryAction
  ) {
    return this.pricingCountryService
      .updatePricingCountry(countryId, body)
      .pipe(
        switchMap(() => dispatch(new GetPricingCountryAction(countryId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(AddPricingCountryBundlePriceAction)
  AddPricingCountryBundlePriceAction(
    { dispatch }: StateContext<PricingCountryStateModel>,
    {
      countryId,
      bundleId,
      price,
      priceType,
      validFrom,
    }: AddPricingCountryBundlePriceAction
  ) {
    return this.pricingCountryService
      .addBundlePriceToPricingCountry(
        countryId,
        bundleId,
        price,
        priceType,
        validFrom
      )
      .pipe(
        switchMap(() => dispatch(new GetPricingCountryAction(countryId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(UpdatePricingCountryBundlePriceAction)
  UpdatePricingCountryBundlePriceAction(
    { dispatch }: StateContext<PricingCountryStateModel>,
    { countryId, pivotId, price, validFrom }: UpdatePricingCountryBundlePriceAction
  ) {
    return this.pricingCountryService
      .updatePricingCountryBundlePrice(countryId, pivotId, price, validFrom)
      .pipe(
        switchMap(() => dispatch(new GetPricingCountryAction(countryId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  @Action(DeletePricingCountryBundlePriceAction)
  DeletePricingCountryBundlePriceAction(
    { dispatch }: StateContext<PricingCountryStateModel>,
    { countryId, pivotId }: DeletePricingCountryBundlePriceAction
  ) {
    return this.pricingCountryService
      .deletePricingCountryBundlePrice(countryId, pivotId)
      .pipe(
        switchMap(() => dispatch(new GetPricingCountryAction(countryId))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }
}

export interface PricingCountryFilters extends ApiFilters {
  countryIds?: string[];
  name?: string;
  currency?: string;
  notes?: string;
  page?: number;
  limit?: number;
}
